import { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useAuth } from '@clerk/react'
import { useQuery } from '@tanstack/react-query'
import { SessionContext, ME_QUERY_KEY } from './contexts.js'
import { useApiClient } from './useApiClient.js'
import { useSubscription } from './useSubscription.js'

// Session bootstrap — fetches GET /api/v1/me when signed in and exposes role
// state under the SAME names the legacy realtime hook exposed, so role-only
// consumers changed one import in Phase 5:
//
//   { isConnected, isAdmin, isSeller, authrole, authextra,
//     needsUsername, clearNeedsUsername, status }
//
// Mapping from SessionMe (docs/api-contract.md §3.1):
//   authrole  = role ('client' | 'seller' | 'admin'; 'anonymous' when signed out)
//   authextra = { clerk_user_id, user_id, display_name }
//   isAdmin   = is_admin; isSeller = role === 'seller' (legacy parity: an admin who
//               also has a seller profile got the admin role, not seller — the
//               full SessionMe payload is exposed as `me` for richer checks)
//
// isConnected semantics (documented choice): true when the session is *settled* —
// either the /me query has resolved for a signed-in user, or Clerk has loaded and
// the user is anonymous. While Clerk is loading or /me is in flight it is false
// (status 'connecting'), mirroring the legacy connection lifecycle so existing
// "wait for connection" guards keep working. A failed /me → status 'error'.
//
// Signed out → anonymous defaults, no fetch.
// Refetches on window focus (TanStack refetchOnWindowFocus) and on
// user.{clerk_user_id}.account events (SessionRealtimeBridge below — it lives
// inside RealtimeProvider because this provider mounts above it).
export function SessionProvider({ children }) {
  const { isLoaded, isSignedIn } = useAuth()
  const client = useApiClient()
  const signedIn = isLoaded && !!isSignedIn

  const query = useQuery({
    queryKey: ME_QUERY_KEY,
    queryFn: ({ signal }) => client.get('/api/v1/me', undefined, { signal }),
    enabled: signedIn,
    staleTime: 15_000,
    refetchOnWindowFocus: 'always',
  })

  // Local override so UsernamePrompt can dismiss immediately after setting the
  // display name, without waiting for the /me refetch (legacy behavior).
  const [usernameOverride, setUsernameOverride] = useState(null)
  useEffect(() => {
    setUsernameOverride(null)
  }, [isSignedIn])

  const clearNeedsUsername = useCallback((newDisplayName) => {
    setUsernameOverride({ displayName: newDisplayName || null })
  }, [])

  const me = signedIn ? query.data ?? null : null
  const { refetch } = query
  const isError = query.isError

  const value = useMemo(() => {
    const base = {
      clearNeedsUsername,
      refetchSession: refetch,
      me,
    }
    if (!signedIn) {
      return {
        ...base,
        status: isLoaded ? 'connected' : 'connecting',
        isConnected: !!isLoaded,
        isAdmin: false,
        isSeller: false,
        authrole: isLoaded ? 'anonymous' : null,
        authextra: null,
        needsUsername: false,
        error: null,
      }
    }
    if (!me) {
      return {
        ...base,
        status: isError ? 'error' : 'connecting',
        isConnected: false,
        isAdmin: false,
        isSeller: false,
        authrole: null,
        authextra: null,
        needsUsername: false,
        error: isError ? query.error : null,
      }
    }
    const displayName = usernameOverride?.displayName ?? me.display_name
    return {
      ...base,
      status: 'connected',
      isConnected: true,
      isAdmin: !!me.is_admin,
      isSeller: me.role === 'seller',
      authrole: me.role,
      authextra: {
        clerk_user_id: me.clerk_user_id,
        user_id: me.user_id,
        display_name: displayName,
      },
      needsUsername: usernameOverride ? false : !!me.needs_username,
      error: null,
    }
  }, [signedIn, isLoaded, me, isError, query.error, usernameOverride, clearNeedsUsername, refetch])

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>
}

// Keeps the session fresh from realtime: refetches /me on any
// user.{clerk_user_id}.account event (role.changed, subscription.changed,
// verification.status_changed) and after every reconnect. Render this INSIDE
// RealtimeProvider (SessionProvider itself mounts above it and cannot call
// useSubscription):
//
//   <SessionProvider>
//     <RealtimeProvider>
//       <SessionRealtimeBridge />
//       ...app
export function SessionRealtimeBridge() {
  const ctx = useContext(SessionContext)
  const clerkUserId = ctx?.authextra?.clerk_user_id
  const refetchSession = ctx?.refetchSession

  useSubscription(
    clerkUserId ? `user.${clerkUserId}.account` : null,
    () => refetchSession?.(),
    { onReconnect: () => refetchSession?.() },
  )
  return null
}
